import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowRight, User, Clock } from 'lucide-react';
import { toast } from 'sonner';

const ActivityFeedPage = () => {
  const { api } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activityType, setActivityType] = useState('all');

  useEffect(() => {
    fetchActivities();
  }, [activityType]);

  const fetchActivities = async () => {
    try {
      const params = new URLSearchParams();
      params.append('limit', '100');
      if (activityType && activityType !== 'all') {
        params.append('activity_type', activityType);
      }
      const response = await api.get(`/activities?${params.toString()}`);
      setActivities(response.data || []);
    } catch (err) {
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'status_change':
        return <ArrowRight className="h-4 w-4" />;
      case 'assignment':
        return <User className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'status_change':
        return 'bg-blue-100 text-blue-600';
      case 'assignment':
        return 'bg-purple-100 text-purple-600';
      case 'call':
        return 'bg-green-100 text-green-600';
      case 'reminder':
        return 'bg-orange-100 text-orange-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="activity-loading">
        <Skeleton className="h-8 w-48" />
        <div className="space-y-4">
          {[...Array(10)].map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="activity-feed-page">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
            Activity Feed
          </h1>
          <p className="text-muted-foreground">
            All activities across the system
          </p>
        </div>
        <Select value={activityType} onValueChange={setActivityType}>
          <SelectTrigger className="w-[180px]" data-testid="activity-type-filter">
            <SelectValue placeholder="All Activities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Activities</SelectItem>
            <SelectItem value="status_change">Status Changes</SelectItem>
            <SelectItem value="assignment">Assignments</SelectItem>
            <SelectItem value="call">Calls</SelectItem>
            <SelectItem value="reminder">Reminders</SelectItem>
            <SelectItem value="note">Notes</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {activities.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Clock className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No activities yet</h3>
            <p className="text-muted-foreground">Activities will appear here as they happen.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {activities.map((activity) => (
            <Card key={activity.id} className="card-hover" data-testid={`activity-${activity.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-full ${getActivityColor(activity.type)}`}>
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{activity.description}</p>
                    <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                      <span>by {activity.author}</span>
                      <span>•</span>
                      <span>{new Date(activity.timestamp).toLocaleString()}</span>
                    </div>
                  </div>
                  <Badge variant="outline" className="capitalize">
                    {activity.type.replace('_', ' ')}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityFeedPage;
